import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Voxoora';
const DEFAULT_DESC = 'Premium audio guides and skip-the-line tickets for museums, historical sites, and walking tours worldwide. Self-guided tours in 10+ languages.';
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1688233923543-7b972d1b171a?w=1200&h=630&fit=crop';

export function SEO({ title, description, image, url, type = 'website', noIndex = false, children }) {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Audio Travel Guides & Skip-the-Line Tickets`;
    const desc = description || DEFAULT_DESC;
    const img = image || DEFAULT_IMAGE;
    const canonical = url || (typeof window !== 'undefined' ? window.location.href : '');

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            <link rel="canonical" href={canonical} />
            {noIndex && <meta name="robots" content="noindex, nofollow" />}

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:image" content={img} />
            <meta property="og:url" content={canonical} />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:locale" content="en_US" />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={img} />

            {children}
        </Helmet>
    );
}

export function CitySchema({ city, url }) {
    const schema = {
        "@context": "https://schema.org",
        "@type": "TouristDestination",
        "name": city.name,
        "description": city.description || `Explore ${city.name}, ${city.country} with premium audio guides and skip-the-line tickets.`,
        "image": city.image_url || '',
        "url": url,
        "containedInPlace": {
            "@type": "Country",
            "name": city.country
        }
    };
    return <script type="application/ld+json">{JSON.stringify(schema)}</script>;
}

export function AttractionSchema({ attraction, url }) {
    const schema = {
        "@context": "https://schema.org",
        "@type": "TouristAttraction",
        "name": attraction.title,
        "description": attraction.description || '',
        "image": attraction.featured_image || '',
        "url": url,
        "address": {
            "@type": "PostalAddress",
            "addressLocality": attraction.city_name || ''
        },
        ...(attraction.audio_duration && { "timeRequired": `PT${attraction.audio_duration}M` })
    };
    return <script type="application/ld+json">{JSON.stringify(schema)}</script>;
}

export function ListingSchema({ listing, url }) {
    const lowestPrice = Math.min(...(listing.options || []).map(o => o.pricing?.adult || 999));
    const schema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": listing.title,
        "description": listing.short_description || listing.description || '',
        "image": listing.featured_image || '',
        "url": url,
        "brand": { "@type": "Brand", "name": "Voxoora" },
        "offers": {
            "@type": "Offer",
            "priceCurrency": (listing.currency || 'EUR').toUpperCase(),
            "price": lowestPrice < 999 ? lowestPrice : 0,
            "availability": "https://schema.org/InStock",
            "url": url
        },
        ...(listing.instant_confirmation && { "additionalProperty": { "@type": "PropertyValue", "name": "Confirmation", "value": "Instant" } })
    };
    return <script type="application/ld+json">{JSON.stringify(schema)}</script>;
}

export function BreadcrumbSchema({ items }) {
    const schema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((item, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": item.name,
            ...(item.url && { "item": item.url })
        }))
    };
    return <script type="application/ld+json">{JSON.stringify(schema)}</script>;
}

export function OrganizationSchema() {
    const schema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Voxoora",
        "description": DEFAULT_DESC,
        "url": typeof window !== 'undefined' ? window.location.origin : '',
        "sameAs": []
    };
    return <script type="application/ld+json">{JSON.stringify(schema)}</script>;
}
